
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { CollapsibleCard } from "@/components/ui/collapsible-card";
import { User, Shield, Pill, AlertCircle } from "lucide-react";
import { TriageData } from "@/types/triage";
import { MedicationInput } from "./MedicationInput";

interface TriageAssessmentProps {
  onComplete: (data: Partial<TriageData>) => void;
  initialData: TriageData;
  isLoading: boolean;
}

const commonMedicalConditions = [
  "Diabetes", "Hypertension", "Asthma", "Heart disease", "Kidney disease",
  "Cancer", "Depression", "Anxiety", "Arthritis", "Allergies"
];

const commonAllergies = [
  "Penicillin", "Aspirin", "Ibuprofen", "Shellfish", "Nuts", "Latex", "Contrast dye"
];

export function TriageAssessment({ onComplete, initialData, isLoading }: TriageAssessmentProps) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [medicalHistory, setMedicalHistory] = useState<string[]>(initialData.medicalHistory);
  const [medications, setMedications] = useState<string[]>(initialData.medications);
  const [allergies, setAllergies] = useState<string[]>(initialData.allergies);
  const [customAllergy, setCustomAllergy] = useState("");
  const [isPregnant, setIsPregnant] = useState(false);
  const [smokingStatus, setSmokingStatus] = useState("");

  const toggleMedicalHistory = (condition: string) => {
    setMedicalHistory(prev => 
      prev.includes(condition) 
        ? prev.filter(c => c !== condition)
        : [...prev, condition]
    );
  };

  const toggleAllergy = (allergy: string) => {
    setAllergies(prev => 
      prev.includes(allergy) 
        ? prev.filter(a => a !== allergy)
        : [...prev, allergy]
    );
  };

  const addCustomAllergy = () => {
    if (customAllergy.trim() && !allergies.includes(customAllergy.trim())) {
      setAllergies([...allergies, customAllergy.trim()]);
      setCustomAllergy("");
    }
  };

  const handleSubmit = () => {
    onComplete({
      age: parseInt(age),
      gender,
      medicalHistory,
      medications,
      allergies,
      isPregnant,
      smokingStatus
    });
  };

  const isComplete = age && gender;

  return (
    <div className="space-y-6">
      {/* Basic Demographics */}
      <CollapsibleCard
        title={
          <div className="flex items-center gap-2">
            <User className="w-5 h-5" />
            <span>Basic Information</span>
          </div>
        }
        description="Help us understand your background"
        defaultOpen={true}
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="age">Age *</Label>
            <Input
              id="age"
              type="number"
              placeholder="Enter your age"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="gender">Gender *</Label>
            <Select value={gender} onValueChange={setGender}>
              <SelectTrigger>
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
                <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {gender === "female" && (
          <div className="mt-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="pregnant" 
                checked={isPregnant}
                onCheckedChange={(checked) => setIsPregnant(checked as boolean)}
              />
              <Label htmlFor="pregnant">Currently pregnant or possibly pregnant</Label>
            </div>
          </div>
        )}

        <div className="mt-4">
          <Label htmlFor="smoking">Smoking Status</Label>
          <Select value={smokingStatus} onValueChange={setSmokingStatus}>
            <SelectTrigger>
              <SelectValue placeholder="Select smoking status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="never">Never smoked</SelectItem>
              <SelectItem value="former">Former smoker</SelectItem>
              <SelectItem value="current">Current smoker</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CollapsibleCard>

      {/* Medical History */}
      <CollapsibleCard
        title={
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            <span>Medical History</span>
          </div>
        }
        description="Select any conditions you have or have had"
        defaultOpen={true}
      >
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {commonMedicalConditions.map((condition) => (
            <div key={condition} className="flex items-center space-x-2">
              <Checkbox 
                id={condition}
                checked={medicalHistory.includes(condition)}
                onCheckedChange={() => toggleMedicalHistory(condition)}
              />
              <Label htmlFor={condition} className="text-sm">
                {condition}
              </Label>
            </div>
          ))}
        </div>
      </CollapsibleCard>

      {/* Current Medications */}
      <CollapsibleCard
        title={
          <div className="flex items-center gap-2">
            <Pill className="w-5 h-5" />
            <span>Current Medications</span>
          </div>
        }
        description="Search and add medications you're currently taking"
        defaultOpen={true}
      >
        <MedicationInput
          medications={medications}
          onMedicationsChange={setMedications}
          placeholder="Search medications (e.g., Lisinopril, Metformin)..."
        />
      </CollapsibleCard>

      {/* Allergies */}
      <CollapsibleCard
        title={
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>Allergies</span>
          </div>
        }
        description="Select any known allergies"
        defaultOpen={true}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {commonAllergies.map((allergy) => (
              <div key={allergy} className="flex items-center space-x-2">
                <Checkbox 
                  id={allergy}
                  checked={allergies.includes(allergy)}
                  onCheckedChange={() => toggleAllergy(allergy)}
                />
                <Label htmlFor={allergy} className="text-sm">
                  {allergy}
                </Label>
              </div>
            ))}
          </div>

          <Separator />

          <div className="flex gap-2">
            <Input
              placeholder="Add other allergies..."
              value={customAllergy}
              onChange={(e) => setCustomAllergy(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addCustomAllergy()}
            />
            <Button onClick={addCustomAllergy}>Add</Button>
          </div>

          {allergies.length > 0 && (
            <div className="space-y-2">
              <Label>Known allergies:</Label>
              <div className="flex flex-wrap gap-2">
                {allergies.map((allergy) => (
                  <span key={allergy} className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm">
                    {allergy}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </CollapsibleCard>

      <div className="flex justify-end">
        <Button 
          onClick={handleSubmit} 
          disabled={!isComplete || isLoading} 
          size="lg"
        >
          {isLoading ? "Analyzing..." : "Complete Assessment"}
        </Button>
      </div>
    </div>
  );
}
